import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  CircularProgress 
 } from '@mui/material';
import { gql, useQuery, useLazyQuery, useMutation } from '@apollo/client'; // Added useMutation 
import DocumentManagementPanel from './DocumentManagementPanel'; 
import ExtractedInfoPanel from './ExtractedInfoPanel'; 
import { DOCUMENT_EXTRACTION_TASKS_BY_DOCUMENT_IDS } from '../../gql/queries/documentExtractionTaskQueries.js';
 
export const GET_DOCUMENTS = gql`
  query GetDocuments {
    documents { 
      id
      name
      userId
      uploadedAt
      status
      size
      type
      summary
      properties
    }
  }
`;

export const UPDATE_DOCUMENT_EXTRACTION_TASK_STATUS = gql`
  mutation UpdateDocumentExtractionTaskStatus($taskId: ID!, $status: String!) {
    updateDocumentExtractionTaskStatus(taskId: $taskId, status: $status) {
      id
      Status
      Text
      Properties
      Assignee
      Dependents
      documentId
      UpdatedBy
      createdAt
      updatedAt
    }
  }
`;

// Helper function to sort documents
function sortDocuments(docs, sortBy) {
  const sorted = [...docs]; 
  sorted.sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    const dateA = a.uploadedAt instanceof Date ? a.uploadedAt.getTime() : 0;
    const dateB = b.uploadedAt instanceof Date ? b.uploadedAt.getTime() : 0;

    if (sortBy === 'name') {
      return nameA.localeCompare(nameB);
    }
    return dateB - dateA;
  });
  return sorted;
}

const UploadDocumentsPage = () => { 
  const [documents, setDocuments] = useState([]); 
  const [sortBy, setSortBy] = useState('date');
  // selectedDocId and approvedExtractions state removed
  const [documentExtractionTasks, setDocumentExtractionTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const [approvedTasks, setApprovedTasks] = useState(new Set()); // To manage approved status for UI
  const [mutatingTaskId, setMutatingTaskId] = useState(null); // Track task being updated
  
  const [needsPolling, setNeedsPolling] = useState(false);

  const { loading: queryLoading, error: queryError, data: queryData, refetch: refetchDocs } = useQuery(GET_DOCUMENTS, {
    fetchPolicy: 'network-only'
  });

  const [fetchTasks, { loading: tasksLazyLoading, error: tasksLazyError, data: tasksData }] = useLazyQuery(DOCUMENT_EXTRACTION_TASKS_BY_DOCUMENT_IDS, {
    fetchPolicy: 'network-only'
  });

  const [updateTaskStatusMutation, { loading: updateTaskLoading, error: updateTaskError }] = useMutation(UPDATE_DOCUMENT_EXTRACTION_TASK_STATUS);

  useEffect(() => {
    if (!queryLoading && queryData && queryData.documents) {
      const processedDocs = queryData.documents.map(doc => {
        let propertiesObj = null;
        if (doc.properties) {
          try {
            if (typeof doc.properties === 'object' && doc.properties !== null) {
              propertiesObj = doc.properties; 
            } else if (typeof doc.properties === 'string') {
              propertiesObj = JSON.parse(doc.properties); 
            }
          } catch (e) {
            console.error("Failed to parse properties JSON for doc:", doc.id, e);
          }
        }
        return {
          ...doc,
          uploadedAt: doc.uploadedAt ? new Date(doc.uploadedAt) : null, 
          parsedProperties: propertiesObj 
        };
      });

      const filteredDocs = processedDocs.filter(doc => doc.uploadedAt !== null);

      const sortedDocs = sortDocuments(filteredDocs, sortBy);

      const shouldPoll = sortedDocs.some(
        doc => doc.status !== 'PROCESSED' && doc.status !== 'ERROR'
      );
      setNeedsPolling(shouldPoll);

      setDocuments(sortedDocs);

    } else if (!queryLoading && queryError) {
      console.log("Condition passed: !queryLoading && queryError");
      setDocuments([]);
      setNeedsPolling(false);
      console.error("Error fetching documents:", queryError);
    }
  }, [queryData, queryError, sortBy, queryLoading]); 

  useEffect(() => {
    if (documents && documents.length > 0) {
      const documentIds = documents.map(doc => doc.id);
      if (documentIds.length > 0) {
        console.log('[UploadDocumentsPage] Fetching tasks for document IDs:', documentIds);
        fetchTasks({ variables: { documentIds } });
      }
    } else {
      // If there are no documents, clear any existing tasks
      setDocumentExtractionTasks([]);
      setTasksLoading(false);
      setTasksError(null);
      console.log('[UploadDocumentsPage] No documents, clearing tasks.');
    }
  }, [documents, fetchTasks]); 

  useEffect(() => {
    if (tasksLazyError) {
      console.error('[UploadDocumentsPage] Error fetching document tasks:', tasksLazyError);
      setTasksError(tasksLazyError);
      setDocumentExtractionTasks([]); // Clear tasks on error
    } else if (tasksData) {
      const tasks = tasksData.documentExtractionTasksByDocumentIds || [];
      setDocumentExtractionTasks(tasks);
      // Initialize approvedTasks based on fetched tasks
      const initiallyApproved = new Set();
      tasks.forEach(task => {
        if (task.Status && task.Status.toLowerCase() === 'approved') {
          initiallyApproved.add(task.id);
        }
      });
      setApprovedTasks(initiallyApproved);
      console.log('[UploadDocumentsPage] Tasks data received, tasks set to:', tasks);
    } else if (!tasksLazyLoading) {
        // If not loading, no error, and no data, it means fetch completed with no tasks or hasn't run for current docs
        // setDocumentExtractionTasks([]); // Avoid clearing if it's just between fetches
    }
    setTasksLoading(tasksLazyLoading);
  }, [tasksData, tasksLazyLoading, tasksLazyError]);

  useEffect(() => {
    if (!needsPolling) {
      console.log("Polling useEffect: needsPolling is false, doing nothing.");
      return; 
    }

    console.log('Polling Interval useEffect: needsPolling is true, setting up interval...');
    const intervalId = setInterval(() => {
      console.log('Polling Interval Tick: Triggering refetchDocs...');
      refetchDocs().catch(err => {
        console.error("Error during polling interval refetch:", err);
      });
    }, 7000); 

    return () => {
      console.log('Polling Interval useEffect: Cleanup - Clearing interval.');
      clearInterval(intervalId);
    };
  }, [needsPolling, refetchDocs]); 

  const sortedDocuments = sortDocuments(documents, sortBy);

  const handleApproveTask = async (taskId) => {
    setMutatingTaskId(taskId);
    try {
      const { data } = await updateTaskStatusMutation({
        variables: { taskId, status: 'approved' },
      });
      if (data && data.updateDocumentExtractionTaskStatus) {
        setDocumentExtractionTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId ? { ...task, Status: 'approved' } : task
          )
        );
        setApprovedTasks(prev => new Set(prev).add(taskId));
        console.log('Task approved:', taskId);
      }
    } catch (err) {
      console.error('Error approving task:', taskId, err);
      // Log the updateTaskError from useMutation hook if available
      if (updateTaskError) {
        console.error('Mutation error (updateTaskError):', updateTaskError);
      }
      alert(`Failed to approve task: ${err.message}`);
    } finally {
      setMutatingTaskId(null);
    }
  };

  const handleRejectTask = async (taskId) => {
    setMutatingTaskId(taskId);
    try {
      const { data } = await updateTaskStatusMutation({
        variables: { taskId, status: 'unapproved' }, // Or 'rejected' if that's a distinct status
      });
      if (data && data.updateDocumentExtractionTaskStatus) {
        setDocumentExtractionTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId ? { ...task, Status: 'unapproved' } : task
          )
        );
        setApprovedTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(taskId);
          return newSet;
        });
        console.log('Task rejected/unapproved:', taskId);
      }
    } catch (err) {
      console.error('Error rejecting task:', taskId, err);
      // Log the updateTaskError from useMutation hook if available
      if (updateTaskError) {
        console.error('Mutation error (updateTaskError):', updateTaskError);
      }
      alert(`Failed to unapprove task: ${err.message}`);
    } finally {
      setMutatingTaskId(null);
    }
  };

  if (queryLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading documents...</Typography>
        </Box>
      </Container>
    );
  }

  if (queryError) {
    return (
     <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">Error loading documents: {queryError.message}</Typography>
      </Container>
    );
 }

  return (
    <Box sx={{ width: '100%' }}>
      <Container maxWidth="xl">
        <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* DocumentManagementPanel always takes a portion of the screen */}
          <Grid item xs={12} md={7}> 
            <DocumentManagementPanel 
              documents={sortedDocuments} 
              onSortChange={setSortBy} 
              currentSortBy={sortBy}
              onUploadSuccess={() => {
                console.log("Upload success, refetching documents...");
                refetchDocs();
              }}
              // Pass setNeedsPolling if DocumentManagementPanel needs to trigger polling based on its actions
              setNeedsPolling={setNeedsPolling} 
            />
          </Grid>
          {/* ExtractedInfoPanel always takes the remaining portion */}
          <Grid item xs={12} md={5} sx={{ height: { xs: 'auto', md: 'calc(100vh - 120px)' } }}>
            <ExtractedInfoPanel 
              documentExtractionTasks={documentExtractionTasks} 
              tasksLoading={tasksLoading}
              tasksError={tasksError}
              onApproveTask={handleApproveTask} // Pass handler
              handleRejectTask={handleRejectTask} // Pass handler
              approvedTasks={approvedTasks} // Pass the set of approved task IDs
              mutatingTaskId={mutatingTaskId} // Pass for disabling button
              isUpdateTaskLoading={updateTaskLoading} // Pass loading state
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default UploadDocumentsPage;